import { useHandler } from "@redotech/react-util/hook";
import {
  rcoOriginLocationSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import EditIcon from "@redotech/redo-icons/edit-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { PartialWithRequiredType } from "../..";
import { AddressEditModal, AddressType } from "./address-edit-modal";

export const OriginLocationInspectorContent = memo(
  function OriginLocationInspectorContent({ step }: { step: RcoSegmentStep }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoOriginLocationSegmentSchema.parse(step.segment),
      [step],
    );

    const { originAddress } = segment;

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Origin location is")}
          </Text>
        </Flex>
        <FormattedOriginAddress originAddress={originAddress} />
      </>
    );
  },
);

export const EditableOriginLocationInspectorContent = memo(
  function EditableOriginLocationInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const [isAddressEditModalOpen, setIsAddressEditModalOpen] = useState(false);

    const segment = useMemo(
      () => rcoOriginLocationSegmentSchema.parse(step.segment),
      [step],
    );

    const handleAddressChange = useHandler((address: AddressType) => {
      const updatedSegment = { ...segment, originAddress: address };
      setStep({ ...step, segment: updatedSegment });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Origin location is")}
          </Text>
        </Flex>
        <FormattedOriginAddress originAddress={segment.originAddress} />
        <Flex justify="flex-end" w="full">
          <RedoButton
            hierarchy="secondary"
            IconLeading={EditIcon}
            onClick={() => setIsAddressEditModalOpen(true)}
            size="xs"
            text={t("Edit")}
          />
        </Flex>
        <AddressEditModal
          address={segment.originAddress}
          isOpen={isAddressEditModalOpen}
          onClose={() => setIsAddressEditModalOpen(false)}
          saveAddress={handleAddressChange}
        />
      </>
    );
  },
);

const FormattedOriginAddress = memo(function FormattedOriginAddress({
  originAddress,
}: {
  originAddress: AddressType;
}) {
  const showCityProvincePostalRow =
    !!originAddress.city ||
    !!originAddress.province ||
    !!originAddress.postalCode;

  return (
    <Flex dir="column" gap="xxs">
      {originAddress.address1 && (
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {originAddress.address1}
        </Text>
      )}
      {originAddress.address2 && (
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {originAddress.address2}
        </Text>
      )}
      {showCityProvincePostalRow && (
        <Flex dir="row" gap="md" wrap="wrap">
          {originAddress.city && (
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {originAddress.city},
            </Text>
          )}
          {originAddress.province && (
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {originAddress.province}
            </Text>
          )}
          {originAddress.postalCode && (
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {originAddress.postalCode}
            </Text>
          )}
        </Flex>
      )}
      {originAddress.country && (
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {originAddress.country}
        </Text>
      )}
    </Flex>
  );
});
